import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Grid, Slider, ThemeProvider, useTheme } from '@mui/material';
import React from 'react';
import { Form, Formik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import Typography from '@mui/material/Typography';
import { CacheProvider } from '@emotion/react';
import { AccountData, AccountService, OrderPageData, PartialLabelData } from '../../../api';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { FormTextField } from '../../formComponents/FormTextField';
import { cacheLtr, useLanguage } from '../../../common/GeneralUtils';
import { FormLabelField } from '../../formComponents/FormLabelField';
import { FormRichTextField } from '../../formComponents/FormRichTextField';
import { clearOrderPageStateFromLocalStorage } from '../../../order_page/orderPageContext/orderPageStateLocalStorage';

type SettingsSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

export type SliderFormValues = {
  id: string;
  type: 'slider';
  min_value: number;
  max_value: number;
  label: PartialLabelData;
};

export type SettingsFormValues = {
  slider: SliderFormValues;
  disclaimers?: PartialLabelData;
};

export const WidgetSection = ({
  orderPage,
  orderPageInitialValues,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: SettingsSectionProps) => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const language = useLanguage();
  const posthog = usePostHog();
  const theme = useTheme();

  const validationSchema = yup.object().shape({
    slider: yup.object().shape({
      min_value: yup.number().required(t('editOrderPage.yup.required')).min(0, t('editOrderPage.yup.hasToBePoitive')),
      max_value: yup
        .number()
        .required(t('editOrderPage.yup.required'))
        .min(0, t('editOrderPage.yup.hasToBePoitive'))
        // can be equal
        .min(yup.ref('min_value'), t('editOrderPage.yup.hasToBeMoreThanMin')),
    }),
  });

  const slider = orderPage.objects.find((x) => x.type === 'slider');
  const initialValues: SettingsFormValues = {
    slider: {
      // @ts-ignore
      min_value: slider?.min_value,
      // @ts-ignore
      max_value: slider?.max_value,
      // @ts-ignore
      label: slider?.label,
      id: slider?.id || '',
      type: 'slider',
    },
    disclaimers: orderPage.disclaimers,
  };

  const handleSubmit = async (values: SettingsFormValues) => {
    try {
      posthog.capture('Editing settings', { section: 'widgetSection', settings: values });
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            ...values,
            id: orderPage.id,
            duration: (orderPageInitialValues.duration || 0) * 60,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
      clearOrderPageStateFromLocalStorage();
      setIsUpdatedSnackOpen(true);
    } catch (e) {
      console.error(e);
    }
  };
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ isSubmitting, dirty, handleSubmit }) => {
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={handleSubmit}>
            <Grid container justifyContent="space-between" flexWrap="nowrap" alignItems="flex-start">
              <Grid container flexDirection="column" gap={1} maxWidth="900px">
                <CacheProvider value={cacheLtr}>
                  <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
                    <Typography>{t('editOrderPage.slider.sliderDescription')}</Typography>
                    <FormLabelField labelFieldName="slider.label" label={''} disabled={formIsDisabled} />
                    <Typography variant="caption">{t('editOrderPage.slider.sliderCaption')}</Typography>
                    <Slider value={2} min={1} max={7} disabled />
                    <Grid
                      mt={-2}
                      container
                      flexWrap="nowrap"
                      flexDirection={language === 'he' ? 'row-reverse' : 'row'}
                      justifyContent="space-between">
                      <Grid item alignContent="flex-start">
                        <Typography variant="caption">{t('editOrderPage.slider.minValue')}</Typography>
                        <FormTextField
                          isFieldUpdate
                          width={70}
                          fieldName="slider.min_value"
                          disabled={formIsDisabled}
                          type="number"
                        />
                      </Grid>
                      <Grid item alignContent="flex-end">
                        <Typography variant="caption">{t('editOrderPage.slider.maxValue')}</Typography>
                        <FormTextField
                          isFieldUpdate
                          width={70}
                          fieldName="slider.max_value"
                          disabled={formIsDisabled}
                          type="number"
                        />
                      </Grid>
                    </Grid>
                  </ThemeProvider>
                </CacheProvider>

                <Grid container flexDirection="column" gap={1} mt={4}>
                  <Typography variant="h6">{t('editOrderPage.disclaimers')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('editOrderPage.disclaimersCaption')}
                  </Typography>
                  <FormRichTextField labelFieldName="disclaimers" label="" disabled={formIsDisabled} />
                </Grid>
              </Grid>
              <LoadingButton
                size="large"
                loading={isSubmitting}
                type="submit"
                variant="contained"
                color="primary"
                disabled={!dirty || formIsDisabled}>
                {t('editOrderPage.orderPageBufferSubmit')}
              </LoadingButton>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
